import type { LayoutMapping } from '../lib/settings-types.js';
import { Field, Select, StatusPill } from './ui/index.js';

/**
 * The column-matching editor shared by Add-stats and the email-report
 * confirmation: each canonical field picks a source column (or none), with a
 * sample value shown so a human can tell which column is which.
 *
 * It is deliberately still one row per OUR field rather than one per source
 * column: our list is twelve and fixed, while a report can arrive with forty
 * columns, and inverting it would make the common case worse to read.
 */
export const CANONICAL_FIELDS: { key: string; label: string; hint?: string; required?: boolean }[] =
  [
    { key: 'date', label: 'Date', hint: 'Rows are dated by the report itself.', required: true },
    { key: 'feedId', label: 'Feed ID' },
    { key: 'country', label: 'Country' },
    { key: 'requests', label: 'Requests' },
    { key: 'searches', label: 'Searches' },
    { key: 'monetizedSearches', label: 'Monetized searches' },
    { key: 'adClicks', label: 'Ad clicks' },
    { key: 'clicks', label: 'Clicks' },
    { key: 'payout', label: 'Revenue / payout' },
    { key: 'subId', label: 'Sub ID' },
    { key: 'coverage', label: 'Coverage' },
    {
      key: 'currency',
      label: 'Currency code',
      hint: 'ISO code column (EUR, USD…), if the report has one.',
    },
  ];

/**
 * What is wrong with a mapping, in words. Exported because the warning belongs
 * next to the fields but the enforcement belongs on the import button — a
 * warning nobody has to act on is how a report gets imported undated.
 */
export function mappingProblems(mapping: LayoutMapping): string[] {
  const used = new Map<string, string[]>();
  for (const f of CANONICAL_FIELDS) {
    const col = mapping.fields[f.key];
    if (col) used.set(col, [...(used.get(col) ?? []), f.label]);
  }
  const problems: string[] = [];
  if (!mapping.fields['date']) {
    problems.push("Pick the column holding the date — without it the rows can't be filed.");
  }
  for (const [col, fields] of used) {
    if (fields.length > 1) problems.push(`“${col}” is being used for ${fields.join(' and ')}.`);
  }
  return problems;
}

interface Props {
  columns: string[];
  samples: Record<string, string>;
  mapping: LayoutMapping;
  onChange: (m: LayoutMapping) => void;
  readOnly?: boolean;
}

export function MappingEditor({ columns, samples, mapping, onChange, readOnly }: Props) {
  const setField = (key: string, col: string) => {
    const fields = { ...mapping.fields, [key]: col || null };
    onChange({ ...mapping, fields });
  };

  // Two columns feeding one field silently mis-files numbers, and a missing
  // date makes the whole import undated. Both were only ever mentioned in a
  // hint; neither was checked.
  const used = new Map<string, string[]>();
  for (const f of CANONICAL_FIELDS) {
    const col = mapping.fields[f.key];
    if (col) used.set(col, [...(used.get(col) ?? []), f.label]);
  }
  const problems = readOnly ? [] : mappingProblems(mapping);

  return (
    <div className="mapping-editor">
      {readOnly ? (
        <p className="ui-field__hint">
          <StatusPill tone="neutral">Built in</StatusPill> This provider&apos;s layout ships with
          the app — there is nothing to choose here.
        </p>
      ) : null}
      {problems.map((text) => (
        <p className="ui-field__error" key={text}>
          {text}
        </p>
      ))}

      <table className="stat-table">
        <thead>
          <tr>
            <th className="l">Our field</th>
            <th className="l">Source column</th>
            <th className="l">Sample</th>
          </tr>
        </thead>
        <tbody>
          {CANONICAL_FIELDS.map((f) => {
            const chosen = mapping.fields[f.key] ?? '';
            const dup = chosen ? (used.get(chosen)?.length ?? 0) > 1 : false;
            return (
              <tr key={f.key}>
                <td className="l">
                  {f.label}
                  {f.required ? <span className="ui-field__req"> *</span> : null}
                  {f.hint ? <div className="table-note">{f.hint}</div> : null}
                </td>
                <td className="l">
                  <Select
                    size="sm"
                    aria-label={`Source column for ${f.label}`}
                    aria-invalid={dup || (f.required && !chosen) ? true : undefined}
                    value={chosen ?? ''}
                    disabled={readOnly}
                    onChange={(e) => setField(f.key, e.target.value)}
                  >
                    <option value="">— not present —</option>
                    {columns.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </Select>
                </td>
                <td className="l table-note">{chosen ? (samples[chosen] ?? '') : ''}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="cfg-grid">
        <Field label="Date format">
          {(aria) => (
            <Select
              {...aria}
              value={mapping.date_format ?? 'iso'}
              disabled={readOnly}
              onChange={(e) =>
                onChange({
                  ...mapping,
                  date_format: e.target.value as NonNullable<LayoutMapping['date_format']>,
                })
              }
            >
              <option value="iso">ISO (2026-08-24)</option>
              <option value="dmy">DD/MM/YYYY</option>
              <option value="mdy">MM/DD/YYYY</option>
              <option value="excel">Excel serial</option>
            </Select>
          )}
        </Field>
      </div>
    </div>
  );
}
